import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { hasNonLatin } from "@/lib/latinName";

interface Props {
  initial: string;
  onCommit: (name: string) => void;
  onCancel: () => void;
}

export function TeamNameEditor({ initial, onCommit, onCancel }: Props) {
  const [value, setValue] = useState(initial);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const commit = () => {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      onCancel();
      return;
    }
    // Hard block: keep team names Latin (no manual transliteration later).
    if (hasNonLatin(trimmed)) return;
    onCommit(trimmed);
  };

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 items-center gap-1">
        <Input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              onCancel();
            }
          }}
          className="h-7 px-2 py-0 text-sm"
          maxLength={80}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onMouseDown={(e) => e.preventDefault()}
          onClick={commit}
          aria-label="Save team name"
        >
          <Check className="h-4 w-4" />
        </Button>
      </div>
      {hasNonLatin(value) ? (
        <p className="text-xs text-destructive">
          Please use Latin letters for your team name.
        </p>
      ) : null}
    </div>
  );
}
